import { CopyToClipboard } from 'react-copy-to-clipboard';
import { currentOrigin } from 'src/app-config';
import { FC, useMemo, useState } from 'react';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { houseToHouseServices, hthHelper } from 'src/services';
import { Hr } from '..';
import { HTHBuildingModalComplex, HTHBuildingModalSimple, HTHBuildingModalToEdit } from '.';
import { HTHHouseholdState } from './HTHHouseholdState';
import { IoIosArrowDroprightCircle } from 'react-icons/io';
import { Modal } from 'react-bootstrap';
import { typeConfigDoc, typeHTHBuilding, typeHTHHousehold, typeHTHFace, typeTerritoryNumber } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetCurrentUser, useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    closeBuildingModalHandler: () => void;
    config: typeConfigDoc;
    currentBuilding: typeHTHBuilding;
    currentFace: typeHTHFace;
    territoryNumber: typeTerritoryNumber;
};

export const HTHBuildingModal: FC<propsType> = ({
    closeBuildingModalHandler, config, currentBuilding, currentFace, territoryNumber
}) => {
    const [copiedToClipboard, setCopiedToClipboard] = useState(false);
    const [doubleStateSelectedHousehold, setDoubleStateSelectedHousehold] = useState<typeHTHHousehold | null>(null);
    const [showEditBuilding, setShowEditBuilding] = useState(false);
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();
    const user = useGetCurrentUser();

    const shareUrl = useMemo(() =>
        hthHelper.GetLinkTextFor30DaysSharing(currentOrigin, config.congregation, territoryNumber, currentFace, currentBuilding)
    , [config, currentBuilding, currentFace, currentOrigin, territoryNumber]);

    const changeCallingStateHandler = async (household: typeHTHHousehold, callingState?: number) => {
        if (!territoryNumber || !currentFace.block || !currentFace.face || !currentBuilding.streetNumber || !household.id) {
            return;
        }
        const isUncheck = (config.isEnabledHthDoubleState && callingState === 0) || (!callingState && (household.isChecked || household.isCheckedNotAnswered));
        if (isUncheck && !hthHelper.IsAllowedToChangeState(household)) {
            return;
        }
        if (typeof callingState === 'undefined') {
            callingState = household.isChecked ? 0 : 1;
        }
        const isSwitchingAmongNoZeroStates = (household.isCheckedNotAnswered && callingState === 1) || (household.isChecked && callingState === 2);
        dispatch(showLoadingModalReducer());
        const response = await houseToHouseServices.ChangeStateToHousehold(config.congregation, territoryNumber, currentFace.block, currentFace.face, currentBuilding.streetNumber, currentBuilding.id, household.id, currentFace.street, callingState, isSwitchingAmongNoZeroStates);
        dispatch(hideLoadingModalReducer());
        if (!response.success) {
            alert(`Falló el cambio de estado de la vivienda: ${response.message.title} | ${response.message.description}`);  // keep alert (modal vs modal)
            return;
        }
        setDoubleStateSelectedHousehold(null);
    }

    const onClickHandler = (currentHousehold: typeHTHHousehold) => {
        if (!config.isEnabledHthDoubleState) {  // normal
            const isUncheck = !currentHousehold.isChecked && !currentHousehold.isCheckedNotAnswered;
            if (!isUncheck && !hthHelper.IsAllowedToChangeState(currentHousehold)) {
                return;
            }
            changeCallingStateHandler(currentHousehold);
        } else {
            const isUncheck = !currentHousehold.isChecked;
            if (!isUncheck && !hthHelper.IsAllowedToChangeState(currentHousehold)) {
                return;
            }
            setDoubleStateSelectedHousehold(currentHousehold);
        }
    }

    const deleteHTHBuildingHandler = () => {
        closeBuildingModalHandler();
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: "¿Eliminar Edificio?",
            message: `Se va a eliminar el edificio ${currentFace.street} ${currentBuilding.streetNumber}. Esta acción es irreversible.`,
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await houseToHouseServices.DeleteBuilding(territoryNumber, currentFace.block, currentFace.face, currentBuilding.streetNumber);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
            }
        }));
    }

    const shareForAMonth = async () => {
        dispatch(showLoadingModalReducer());
        const response = await houseToHouseServices.ShareBuildingForAMonth(territoryNumber, currentFace.block, currentFace.face, currentBuilding.streetNumber);
        dispatch(hideLoadingModalReducer());
        if (!response.success) {
            dispatch(setAlertResponse({ response }));
            return;
        }
        setCopiedToClipboard(true);
    }

    return (
        <Modal
            backdrop={'static'}
            backdropClassName={''}
            contentClassName={'bg-opacity bg-secondary'}
            keyboard={false}
            onHide={closeBuildingModalHandler}
            show
            size={'lg'}
            fullscreen={isMobile || undefined}
        >
            <Modal.Body className={isDarkMode ? 'bg-dark' : ''}>
                <div className={`card ${isDarkMode ? 'bg-dark border' : ''} pb-3`}>
                    {!showEditBuilding && !doubleStateSelectedHousehold &&
                        <button className={'btn btn-general-blue btn-size12 w-100 mx-auto mt-4 mb-2'}
                            onClick={closeBuildingModalHandler}
                            style={{ maxWidth: '250px' }}
                        >
                            Cerrar{currentBuilding.isComplex ? ' Edificio' : ''}
                        </button>
                    }
                    <div className={`bg-lightdark text-white ${doubleStateSelectedHousehold ? 'mt-5' : 'mt-3'} mx-1`}>
                        <h1 className={'border-top border-start border-end text-center fw-bolder pt-2 pb-1 mb-0'}
                            style={{ fontSize: '1.6rem' }}
                        >
                            {!isMobile && 'Edificio '}{currentFace.street} {currentBuilding.streetNumber}{currentBuilding.streetNumber2 ? `/${currentBuilding.streetNumber2}` : ''}{currentBuilding.streetNumber3 ? `/${currentBuilding.streetNumber3}` : ''}
                        </h1>
                        <div className={'border-bottom border-start border-end text-center pb-2'}
                            style={{ fontSize: '.9rem' }}
                        >
                            {doubleStateSelectedHousehold ? '' : `Últ. vez: ${hthHelper.GetLastTimeOfBuilding(currentBuilding)}`}
                        </div>
                    </div>
                    {showEditBuilding ?
                        <HTHBuildingModalToEdit
                            closeEdit={() => setShowEditBuilding(false)}
                            currentBuilding={currentBuilding}
                            currentFace={currentFace}
                            territoryNumber={territoryNumber}
                        />
                        :
                        <>
                        {isMobile && currentBuilding.numberPerLevel > 3 && !doubleStateSelectedHousehold &&
                            <div className={'bg-green text-white text-center small py-1 mt-3 mx-2'}
                                style={{ "--bs-bg-opacity": 1 } as React.CSSProperties}
                            >
                                Hay más timbres a la derecha <IoIosArrowDroprightCircle />
                            </div>
                        }
                        {(config.isEnabledHthDoubleState && !doubleStateSelectedHousehold) &&
                            <div className={'text-center d-flex justify-content-center mt-4'}>
                                <div className={'bg-yellow text-white text-center rounded small w-75 py-1'}
                                    style={{ "--bs-bg-opacity": 1 } as React.CSSProperties}
                                >
                                    Rojo: atendieron | Amarillo: no atendieron
                                </div>
                            </div>
                        }
                        {doubleStateSelectedHousehold ?
                            <HTHHouseholdState
                                changeCallingStateHandler={changeCallingStateHandler}
                                closeHandler={() => {
                                    setDoubleStateSelectedHousehold(null);
                                }}
                                selectedHousehold={doubleStateSelectedHousehold}
                            />
                            :
                            currentBuilding.isComplex ?
                                <HTHBuildingModalComplex
                                    currentBuilding={{...currentBuilding, street: currentFace.street}}
                                    onClickHandler={onClickHandler}
                                />
                                :
                                <HTHBuildingModalSimple
                                    currentBuilding={{...currentBuilding, street: currentFace.street}}
                                    onClickHandler={onClickHandler}
                                />
                        }
                        {!doubleStateSelectedHousehold &&
                            <>
                            <Hr />
                            <button className={'btn btn-general-blue btn-size12 w-100 mx-auto my-2'}
                                style={{ maxWidth: '250px' }}
                                onClick={closeBuildingModalHandler}
                            >
                                Cerrar{currentBuilding.isComplex ? ' Edificio' : ''}
                            </button>
                            </>
                        }
                        {user.isBuildingsAdmin && !showEditBuilding && !doubleStateSelectedHousehold &&
                            <>
                            <Hr />
                            <button className={'btn btn-yellow btn-size12 w-100 mx-auto my-1'}
                                style={{ maxWidth: '300px' }}
                                onClick={() => setShowEditBuilding(true)}
                            >
                                Editar Ubicación/Numeración
                            </button>
                            <button className={'btn btn-general-red btn-size12 w-100 mx-auto my-1'}
                                style={{ maxWidth: '300px' }}
                                onClick={() => deleteHTHBuildingHandler()}
                            >
                                Eliminar
                            </button>
                            </>
                        }
                        {user.isHthAdmin && currentBuilding.households.length > 9 && !doubleStateSelectedHousehold &&
                            <>
                            <Hr />
                            <CopyToClipboard text={shareUrl} onCopy={() => setCopiedToClipboard(true)}>
                                <button className={`btn btn-general-${copiedToClipboard ? 'red' : 'blue'} btn-size12 w-100 mx-auto mt-2 mb-4`}
                                    style={{ maxWidth: '300px' }}
                                    onClick={shareForAMonth}
                                >
                                    {copiedToClipboard ? 'Link copiado!' : 'Compartir por 30 días'}
                                </button>
                            </CopyToClipboard>
                            </>
                        }
                        </>
                    }
                </div>
            </Modal.Body>
        </Modal>
    );
}
